import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Logo from '../components/Logo';

const CandidateSetup = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUserInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/candidate-setup/${token}`
      );
      setUserInfo(response.data.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid or expired setup link');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/candidate-setup`,
        { token, password }
      );

      // Store token and user data
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));

      toast.success('Account setup completed! Redirecting to dashboard...');
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete setup');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="card" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="card-body text-center p-5">
            <i className="bi bi-exclamation-circle text-danger" style={{ fontSize: '4rem' }}></i>
            <h3 className="mt-3">Setup Link Invalid</h3>
            <p className="text-muted">{error}</p>
            <p className="text-muted">Please contact your administrator for a new setup link.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="text-center mb-4">
              <Logo color="white" width="250px" />
            </div>

            <div className="card shadow-lg" style={{ borderRadius: '12px', border: 'none' }}>
              <div className="card-body p-5">
                <h3 className="text-center mb-1" style={{ fontWeight: '700', color: '#2c3e50' }}>
                  Complete Your Account Setup
                </h3>
                <p className="text-center text-muted mb-4">Set your password to get started</p>

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={userInfo?.fullName || ''}
                      disabled
                      style={{ backgroundColor: '#f3f4f6' }}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={userInfo?.email || ''}
                      disabled
                      style={{ backgroundColor: '#f3f4f6' }}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>
                      Password *
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      minLength={6}
                    />
                    <small className="text-muted">Must be at least 6 characters</small>
                  </div>

                  <div className="mb-4">
                    <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn w-100 py-3"
                    disabled={submitting}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '1rem',
                      borderRadius: '8px'
                    }}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Setting up...
                      </>
                    ) : (
                      'Complete Setup'
                    )}
                  </button>
                </form>

                <div className="text-center mt-4">
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    <i className="bi bi-shield-check me-1"></i>
                    Your information is secure and encrypted
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateSetup;
